import { useMemo, useState } from "react";
import styled from "styled-components";

import { useUserChats } from "../api/hooks";
import type { ChatSummary } from "../api/types";
import { ChatRow } from "../components/ChatRow";
import { EmptyChats } from "../components/EmptyChats";
import { ErrorNotice, Loading, SectionLabel } from "../components/primitives";
import { getBotUsername, openTelegramLink } from "../telegram/sdk";

// The no-context picker renders outside the routed Shell, so it owns its own
// padding. No TabBar here — there's no chat to navigate yet.
const Screen = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(4)};
  padding: ${({ theme }) => theme.space(4)};
  padding-bottom: ${({ theme }) => theme.space(10)};
`;

const HeadRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const Mono = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  color: ${({ theme }) => theme.color.textFaint};
`;

const MonoDim = styled(Mono)`
  color: ${({ theme }) => theme.color.textDim};
`;

const Search = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.border};

  .icon {
    font-family: ${({ theme }) => theme.font.mono};
    font-size: 14px;
    color: ${({ theme }) => theme.color.textFaint};
  }

  input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: none;
    color: ${({ theme }) => theme.color.text};
    font-family: ${({ theme }) => theme.font.ui};
    font-size: 14px;

    &::placeholder {
      color: ${({ theme }) => theme.color.textFaint};
    }
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const NoteCard = styled.div`
  padding: ${({ theme }) => theme.space(4)};
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px dashed ${({ theme }) => theme.color.borderHi};
  color: ${({ theme }) => theme.color.textDim};
  font-size: 13px;
  text-align: center;
`;

const BoardLink = styled.button`
  appearance: none;
  width: 100%;
  margin-top: ${({ theme }) => theme.space(2)};
  padding: 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.color.border};
  color: ${({ theme }) => theme.color.textDim};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  cursor: pointer;

  &:active {
    transform: scale(0.99);
  }
`;

function matches(chat: ChatSummary, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return chat.title.toLowerCase().includes(q) || (chat.username?.toLowerCase().includes(q) ?? false);
}

export function ChooseChat({
  onSelectChat,
  onViewBoard,
}: {
  onSelectChat: (chatId: number) => void;
  onViewBoard: () => void;
}) {
  const { data, isLoading, error, refetch } = useUserChats();
  const [query, setQuery] = useState("");

  const joined = useMemo(
    () => (data?.joined ?? []).filter((c) => matches(c, query)),
    [data, query],
  );
  const discover = useMemo(
    () => (data?.discover ?? []).filter((c) => matches(c, query)),
    [data, query],
  );

  function join(chat: ChatSummary) {
    if (chat.username) openTelegramLink(`https://t.me/${chat.username}`);
  }

  function addToGroup() {
    const bot = getBotUsername();
    if (bot) openTelegramLink(`https://t.me/${bot}?startgroup=rocky`);
  }

  // True empty state: the user has no chats and there are none to discover.
  const noData = Boolean(data && data.joined.length === 0 && data.discover.length === 0);

  return (
    <Screen>
      <HeadRow>
        <Mono>{"// choose a chat"}</Mono>
        <MonoDim>{data?.joined.length ?? 0} joined</MonoDim>
      </HeadRow>

      <Search>
        <span className="icon">⌕</span>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="search chats with Rocky…"
          aria-label="search chats with Rocky"
        />
      </Search>

      {isLoading && <Loading label="finding your chats…" />}
      {error && <ErrorNotice message={error.message} />}

      {noData && <EmptyChats onBrowse={() => void refetch()} onAddToGroup={addToGroup} />}

      {data && !noData && (
        <>
          <div>
            <SectionLabel>{"// your chats"}</SectionLabel>
            {joined.length > 0 ? (
              <List>
                {joined.map((c) => (
                  <ChatRow key={c.chatId} chat={c} onOpen={onSelectChat} onJoin={join} />
                ))}
              </List>
            ) : (
              <NoteCard>
                {query ? "no joined chats match" : "you haven't joined any chats with Rocky yet"}
              </NoteCard>
            )}
          </div>

          <div>
            <SectionLabel>{"// discover"}</SectionLabel>
            {discover.length > 0 ? (
              <List>
                {discover.map((c) => (
                  <ChatRow key={c.chatId} chat={c} onOpen={onSelectChat} onJoin={join} />
                ))}
              </List>
            ) : (
              <NoteCard>
                {query ? "no chats match" : "no public chats to discover right now"}
              </NoteCard>
            )}
          </div>

          <BoardLink type="button" onClick={onViewBoard}>
            {"// view global leaderboard →"}
          </BoardLink>
        </>
      )}
    </Screen>
  );
}
